import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import {
    Image as ImageIcon, User as UserIcon, Sparkles, Database,
    Trash2, RefreshCw, AlertTriangle, Loader2, CheckCircle2, ShieldCheck,
    Globe2, Plug, Clock, Wand2, FileEdit, Languages,
    Zap, UserCheck, Users as UsersIcon, HeartHandshake,
    Gauge, Shield, Brain, Send, BarChart3, Rocket, Briefcase, Linkedin,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

const TIMEZONES = [
    "Europe/Brussels", "Europe/Amsterdam", "Europe/Paris", "Europe/London",
    "Europe/Berlin", "Europe/Madrid", "UTC", "America/New_York",
];

export default function WorkspaceSettings() {
    const { t } = useTranslation();
    const { user, workspace, refreshWorkspace } = useAuth();
    const isAdmin = user?.role === "owner" || user?.role === "admin";
    const [dataStatus, setDataStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [restarting, setRestarting] = useState(false);

    const fetchStatus = async () => {
        try {
            const { data } = await api.get("/workspace-settings/data-status");
            setDataStatus(data);
        } catch (e) { setDataStatus(null); }
    };
    useEffect(() => { if (isAdmin) fetchStatus(); }, [isAdmin]);

    const patch = async (changes) => {
        setBusy(true);
        try {
            await api.patch("/workspace-settings", changes);
            await refreshWorkspace?.();
            toast.success(i18n.t("common.updated") + ".");
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    const restartOnboarding = async () => {
        if (!window.confirm(t("setup.restart_confirm"))) return;
        setRestarting(true);
        try {
            await api.post("/onboarding/restart");
            await refreshWorkspace?.();
            toast.success(t("setup.restarted"));
            localStorage.removeItem("jr_setup_dismissed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("common.failed"));
        } finally {
            setRestarting(false);
        }
    };

    if (!isAdmin) {
        return (
            <PageHeader title={i18n.t("wsettings.title")} subtitle={i18n.t("wsettings.admin_only")}>
                <div className="text-sm text-[var(--jr-text-2)]">{i18n.t("wsettings.no_access")}</div>
            </PageHeader>
        );
    }

    return (
        <PageHeader title={i18n.t("wsettings.title")}
            subtitle={`${workspace?.name} · ${workspace?.tier} tier · ${workspace?.credits} credits`}
            actions={
                <button onClick={restartOnboarding} disabled={restarting} data-testid="restart-onboarding"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--jr-brand)]/40 bg-[var(--jr-brand)]/10 text-sm text-[var(--jr-brand)] hover:bg-[var(--jr-brand)]/20 disabled:opacity-60">
                    {restarting ? <Loader2 className="animate-spin" size={14}/> : <Rocket size={14}/>}
                    {t("setup.restart")}
                </button>
            }>
            <div className="space-y-6 max-w-3xl">
                {/* Photo style */}
                <Section icon={ImageIcon} title={i18n.t("wsettings.avatar_title")}
                    description={i18n.t("wsettings.avatar_desc")}>
                    <div className="grid grid-cols-2 gap-3">
                        <StyleCard
                            value="illustrated"
                            active={workspace?.photo_style !== "stock"}
                            onPick={() => patch({ photo_style: "illustrated" })}
                            disabled={busy}
                            title="Illustrated (default)"
                            sub={i18n.t("wsettings.illustrated_sub")}
                            sampleSeeds={["vincent-peeters", "hannah-claes", "emma-hofman"]}
                            style="illustrated"
                            testid="photo-style-illustrated"
                        />
                        <StyleCard
                            value="stock"
                            active={workspace?.photo_style === "stock"}
                            onPick={() => patch({ photo_style: "stock" })}
                            disabled={busy}
                            title="Stock photos"
                            sub={i18n.t("wsettings.stock_sub")}
                            sampleSeeds={["men/85", "women/68", "women/12"]}
                            style="stock"
                            testid="photo-style-stock"
                        />
                    </div>
                </Section>

                {/* Live demo mode */}
                <Section icon={Sparkles} title={i18n.t("wsettings.demo_title")}
                    description={i18n.t("wsettings.demo_desc")}>
                    <ToggleRow
                        checked={!!workspace?.demo_mode}
                        onChange={(v) => patch({ demo_mode: v })}
                        disabled={busy}
                        label={i18n.t("wsettings.demo_label")}
                        sub={workspace?.demo_mode ? i18n.t("wsettings.demo_on") : i18n.t("wsettings.demo_off")}
                        testid="demo-mode-toggle"
                    />
                </Section>

                {/* Timezone — all timestamps in the app render in this zone */}
                <Section icon={Clock} title={i18n.t("wsettings.tz_title")}
                    description={i18n.t("wsettings.tz_desc")}>
                    <select
                        value={workspace?.timezone || "Europe/Brussels"}
                        onChange={(e) => patch({ timezone: e.target.value })}
                        disabled={busy}
                        data-testid="timezone-select"
                        className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2 text-sm text-white outline-none focus:border-white/25">
                        {TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>{tz}</option>
                        ))}
                    </select>
                </Section>

                {/* Outreach approval workflow */}
                <ApprovalSettingsSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Sprint 5 · #18 Auto-approval van veilige drafts */}
                <AutoApprovalSection />

                {/* Sprint 5 · #9 Cronos re-engage */}
                <CronosReengageSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Sprint 5 · #17 Pre-warm outreach */}
                <PrewarmSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Sprint 4b · #15 Diversity-lens defaults */}
                <DiversityLensSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Sprint 6 · #20 Rate-limit governor */}
                <GovernorSection />

                {/* Sprint 6 · #21 GDPR auto-purge */}
                <GdprSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Sprint 6 · #5 Auto-ICP + #19 Freshness */}
                <Sprint6ExtrasSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Outreach language (Tone-of-Voice) */}
                <LanguageSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Auto-Throttle Pacing */}
                <PacingSettingsSection />

                {/* Auto-Enrich Pipeline */}
                <AutoEnrichSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Auto-Draft Outreach */}
                <AutoDraftSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Data sources for Sales Hunter */}
                <DataSourcesSection workspace={workspace} refresh={refreshWorkspace} />

                {/* Profile Resolver agent */}
                <ProfileResolverSection />

                {/* Data seeding */}
                <Section icon={Database} title="Demo data" danger
                    description={i18n.t("wsettings.danger_desc")}>
                    {dataStatus && (
                        <div className="mb-3 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3">
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">
                                Huidige inhoud · {dataStatus.total} docs
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                                {Object.entries(dataStatus.collections).map(([k, v]) => (
                                    <div key={k} className="flex justify-between text-[var(--jr-text-2)]">
                                        <span>{k}</span><span className="text-white font-mono">{v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <ReseedButton onDone={fetchStatus} />
                        <PurgeButton workspaceName={workspace?.name} onDone={fetchStatus} />
                    </div>
                </Section>
            </div>
        </PageHeader>
    );
}

/* ----- helpers ----- */
function Section({ icon: Icon, title, description, children, danger }) {
    return (
        <div className={`rounded-xl border p-5 ${danger ? "border-[var(--jr-danger)]/25 bg-[var(--jr-danger)]/[0.03]" : "border-white/8 bg-[var(--jr-surface)]"}`}>
            <div className="flex items-start gap-3 mb-4">
                <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${danger ? "bg-[var(--jr-danger)]/15 text-[var(--jr-danger)]" : "bg-white/[0.04] text-[var(--jr-brand)]"}`}>
                    <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg tracking-tight text-white">{title}</h3>
                    <p className="text-xs text-[var(--jr-text-2)] mt-0.5 leading-relaxed">{description}</p>
                </div>
            </div>
            {children}
        </div>
    );
}

function ToggleRow({ checked, onChange, disabled, label, sub, testid }) {
    return (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2.5">
            <div className="flex-1 min-w-0">
                <div className="text-sm text-white">{label}</div>
                <div className="text-[11px] text-[var(--jr-text-3)] mt-0.5">{sub}</div>
            </div>
            <button type="button" role="switch" aria-checked={checked} disabled={disabled}
                onClick={() => onChange(!checked)} data-testid={testid}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? "bg-[var(--jr-brand)]" : "bg-white/10"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
            </button>
        </div>
    );
}

function StyleCard({ value, active, onPick, disabled, title, sub, sampleSeeds, style, testid }) {
    const url = (seed) => style === "stock"
        ? `https://randomuser.me/api/portraits/${seed}.jpg`
        : `https://api.dicebear.com/9.x/notionists-neutral/svg?seed=${seed}&backgroundColor=f1f5f9&radius=50`;
    return (
        <button type="button" onClick={onPick} disabled={disabled} data-testid={testid}
            className={`text-left rounded-lg border-2 p-3 transition-all ${active ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/5" : "border-white/10 hover:border-white/25 hover:bg-white/[0.02]"}`}>
            <div className="flex items-center gap-2 mb-2">
                {sampleSeeds.map((s) => (
                    <img key={s} src={url(s)} alt="" className="w-8 h-8 rounded-full bg-white/5 border border-white/10 object-cover" />
                ))}
                {active && <CheckCircle2 size={14} className="ml-auto text-[var(--jr-brand)]" />}
            </div>
            <div className="text-sm text-white">{title}</div>
            <div className="text-[11px] text-[var(--jr-text-3)]">{sub}</div>
        </button>
    );
}

function DataSourcesSection({ workspace, refresh }) {
    const [busy, setBusy] = useState(false);
    const [integrations, setIntegrations] = useState(null);   // null = status onbekend
    const ds = workspace?.data_sources || { llm: true, apollo: false, perplexity: false };

    // Echte key-status ophalen uit de Integraties-vault zodat we nooit
    // "API key vereist" tonen terwijl de key al gekoppeld is.
    useEffect(() => {
        api.get("/integrations")
            .then((r) => setIntegrations(r.data))
            .catch(() => setIntegrations(null));   // bv. 403 voor non-admin → statische hint
    }, []);

    const toggle = async (key) => {
        setBusy(true);
        try {
            await api.patch("/workspace-settings/data-sources", { [key]: !ds[key] });
            await refresh?.();
            toast.success(`${key.toUpperCase()} ${!ds[key] ? "ingeschakeld" : "uitgeschakeld"}.`);
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    const SOURCES = [
        {
            id: "llm", label: "LLM (Claude)",
            icon: Sparkles, color: "var(--jr-brand)",
            note: i18n.t("wsettings.src_llm"),
            provider: null,
        },
        {
            id: "apollo", label: "Apollo.io",
            icon: Plug, color: "var(--jr-info)",
            note: i18n.t("wsettings.src_apollo"),
            provider: "apollo",
        },
        {
            id: "apollo_mcp", label: "Apollo MCP (OAuth)",
            icon: Plug, color: "#f59e0b",
            note: i18n.t("wsettings.src_apollo_mcp"),
            provider: null,
        },
        {
            id: "perplexity", label: "Perplexity Sonar Pro",
            icon: Globe2, color: "var(--jr-success)",
            note: i18n.t("wsettings.src_perplexity"),
            provider: "perplexity",
        },
        {
            id: "serper", label: "Serper (Google)",
            icon: Globe2, color: "#22c55e",
            note: i18n.t("wsettings.src_serper"),
            provider: "serper",
        },
        {
            id: "linkedin_jobs", label: "LinkedIn Jobs (Bright Data)",
            icon: Briefcase, color: "#4c8bf5",
            note: i18n.t("wsettings.src_linkedin_jobs"),
            provider: "brightdata",
        },
        {
            id: "apify_leads", label: "Apify Leads Finder",
            icon: Database, color: "#97d700",
            note: i18n.t("wsettings.src_apify_leads"),
            provider: "apify",
        },
        {
            id: "apify_jobs", label: "LinkedIn Jobs (Apify)",
            icon: Briefcase, color: "#97d700",
            note: i18n.t("wsettings.src_apify_jobs"),
            provider: "apify",
        },
        {
            id: "google_maps", label: "Google Maps (Apify)",
            icon: Globe2, color: "#34a853",
            note: i18n.t("wsettings.src_google_maps"),
            provider: "apify",
        },
        {
            id: "zevari", label: "Zevari (LinkedIn Signal-scan)",
            icon: Plug, color: "#0a66c2",
            note: i18n.t("wsettings.src_zevari"),
            provider: "zevari",
        },
        {
            id: "scaliq", label: "ScalIQ (LinkedIn People-search)",
            icon: Plug, color: "#6366f1",
            note: i18n.t("wsettings.src_scaliq"),
            provider: "scaliq",
        },
    ];

    return (
        <Section icon={Database} title={i18n.t("wsettings.ds_title")}
            description={i18n.t("wsettings.ds_desc")}>
            <div className="grid sm:grid-cols-3 gap-2">
                {SOURCES.map((s) => {
                    const active = !!ds[s.id];
                    const Icon = s.icon;
                    // Key-status: true/false uit de vault, of null als onbekend
                    const keyStatus = s.provider ? (integrations ? !!integrations[s.provider]?.configured : null) : undefined;
                    return (
                        <button key={s.id} type="button" onClick={() => toggle(s.id)} disabled={busy}
                            data-testid={`datasource-${s.id}`}
                            className={`text-left rounded-lg border-2 p-3 transition-all ${active
                                ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/15 shadow-[0_0_0_3px_rgba(255,140,42,0.08)]"
                                : "border-white/8 bg-[var(--jr-surface)] hover:border-white/20"}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Icon size={14} style={{ color: active ? "var(--jr-brand)" : s.color }} />
                                    <span className="text-sm font-medium text-white">{s.label}</span>
                                </div>
                                {active
                                    ? <CheckCircle2 size={14} className="text-[var(--jr-brand)]" />
                                    : <span className="w-3.5 h-3.5 rounded-full border border-white/20" />}
                            </div>
                            <div className="text-[11px] text-[var(--jr-text-3)] mt-1.5 leading-snug">{s.note}</div>
                            {s.provider && keyStatus === true && (
                                <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#22c55e]"
                                    data-testid={`keystatus-ok-${s.id}`}>
                                    ✓ {i18n.t("wsettings.key_linked")}{integrations?.[s.provider]?.key_source === "env" ? " · server" : ""}
                                </div>
                            )}
                            {s.provider && keyStatus === false && (
                                <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-amber)]"
                                    data-testid={`keystatus-missing-${s.id}`}>
                                    🔑 API key vereist — voeg toe via Integraties
                                </div>
                            )}
                            {s.provider && keyStatus === null && (
                                <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                                    🔑 Vereist API key (beheer via Integraties)
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
            <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                Tip: laat LLM aan staan als fallback — wanneer Apollo/Perplexity een rate-limit raken, valt Sales Hunter automatisch terug op Claude.
            </div>
        </Section>
    );
}

function ApprovalSettingsSection({ workspace, refresh }) {
    const [busy, setBusy] = useState(false);
    const required = !!workspace?.approval_required;
    const roles = workspace?.approver_roles || ["owner", "admin"];

    const patch = async (changes) => {
        setBusy(true);
        try {
            await api.patch("/outreach/settings/approval", changes);
            await refresh?.();
            toast.success(i18n.t("wsettings.approval_updated"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    const toggleRole = (r) => {
        const next = roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r];
        if (next.length === 0) { toast.error(i18n.t("wsettings.role_min")); return; }
        patch({ approver_roles: next });
    };

    const ROLE_OPTIONS = [
        { id: "owner",     label: "Owner",         sub: i18n.t("wsettings.role_owner_sub") },
        { id: "admin",     label: "Admin",         sub: "Administrators" },
        { id: "on_behalf", label: i18n.t("wsettings.role_onbehalf_label"), sub: i18n.t("wsettings.role_onbehalf_sub") },
    ];

    return (
        <Section icon={ShieldCheck} title="Outreach approval workflow"
            description={i18n.t("wsettings.approval_desc")}>
            <ToggleRow
                checked={required}
                onChange={(v) => patch({ approval_required: v })}
                disabled={busy}
                label={i18n.t("wsettings.approval_label")}
                sub={required
                    ? i18n.t("wsettings.approval_on")
                    : i18n.t("wsettings.approval_off")}
                testid="approval-required-toggle"
            />
            {required && (
                <div className="mt-3 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">
                        Wie mag goedkeuren?
                    </div>
                    <div className="grid sm:grid-cols-3 gap-2">
                        {ROLE_OPTIONS.map((opt) => {
                            const active = roles.includes(opt.id);
                            return (
                                <button key={opt.id} type="button" onClick={() => toggleRole(opt.id)} disabled={busy}
                                    data-testid={`approver-role-${opt.id}`}
                                    className={`text-left rounded-lg border-2 p-3 transition-all ${active
                                        ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/15 text-white shadow-[0_0_0_3px_rgba(255,140,42,0.08)]"
                                        : "border-white/8 bg-[var(--jr-surface)] text-[var(--jr-text-2)] hover:border-white/20 hover:bg-white/[0.04]"}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">{opt.label}</span>
                                        {active
                                            ? <CheckCircle2 size={14} className="text-[var(--jr-brand)]" />
                                            : <span className="w-3.5 h-3.5 rounded-full border border-white/20" />}
                                    </div>
                                    <div className="text-[11px] text-[var(--jr-text-3)] mt-1">{opt.sub}</div>
                                    {active && (
                                        <div className="mt-2 text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--jr-brand)]">
                                            ✓ Mag goedkeuren
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </Section>
    );
}

function ReseedButton({ onDone }) {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        if (!confirm(i18n.t("wsettings.reseed_confirm"))) return;
        setBusy(true);
        try {
            await api.post("/workspace-settings/reseed-demo-data");
            toast.success(i18n.t("wsettings.reseed_done"));
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    return (
        <button onClick={run} disabled={busy} data-testid="reseed-data"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/[0.04] text-sm text-white hover:bg-white/[0.08] disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
            Re-seed demo data
        </button>
    );
}

function PurgeButton({ workspaceName, onDone }) {
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/workspace-settings/purge-demo-data", { confirm: confirmText });
            const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
            toast.success(i18n.t("wsettings.purged_docs", { total }));
            setOpen(false); setConfirmText("");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    return (
        <>
            <button onClick={() => setOpen(true)} data-testid="purge-data"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--jr-danger)]/35 bg-[var(--jr-danger)]/[0.08] text-sm text-[var(--jr-danger)] hover:bg-[var(--jr-danger)]/[0.14]">
                <Trash2 size={14} /> {i18n.t("wsettings.delete_all")}
            </button>
            {open && (
                <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
                    onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
                    <div className="w-full max-w-md bg-[var(--jr-surface)] border border-[var(--jr-danger)]/30 rounded-2xl p-6 jr-fadein max-h-[calc(100dvh-2rem)] overflow-y-auto"
                        data-testid="purge-modal">
                        <div className="flex items-start gap-3 mb-3">
                            <AlertTriangle size={20} className="text-[var(--jr-danger)] shrink-0" />
                            <div>
                                <h3 className="font-display text-xl tracking-tight">Onomkeerbaar</h3>
                                <p className="text-xs text-[var(--jr-text-2)] mt-1">
                                    {i18n.t("wsettings.purge_modal_pre")} <span className="text-white">{workspaceName}</span> {i18n.t("wsettings.purge_modal_post")}
                                </p>
                                <p className="text-xs text-[var(--jr-text-2)] mt-2">
                                    Type de workspace-naam <span className="text-white font-mono">&ldquo;{workspaceName}&rdquo;</span> om te bevestigen.
                                </p>
                            </div>
                        </div>
                        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={workspaceName} data-testid="purge-confirm-input"
                            className="w-full mt-2 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-danger)] outline-none font-mono" />
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => { setOpen(false); setConfirmText(""); }} disabled={busy}
                                className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                            <button onClick={run} disabled={busy || confirmText.trim() !== workspaceName}
                                data-testid="purge-confirm-submit"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-danger)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-40">
                                {busy ? <><Loader2 className="animate-spin" size={14} /> {i18n.t("wsettings.deleting")}</> : <><Trash2 size={14} /> {i18n.t("wsettings.delete_permanent")}</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  Auto-Throttle Pacing settings                                      */
/* ------------------------------------------------------------------ */
function PacingSettingsSection() {
    const [pacing, setPacing] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/outreach/settings/pacing");
            setPacing(data.send_pacing || {});
        } catch (e) { toast.error(i18n.t("wsettings.pacing_load_failed")); }
    };
    useEffect(() => { load(); }, []);

    const save = async (changes) => {
        setBusy(true);
        try {
            const { data } = await api.patch("/outreach/settings/pacing", changes);
            setPacing(data.send_pacing);
            toast.success(i18n.t("wsettings.pacing_updated"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    if (!pacing) return null;
    const isThrottled = pacing.mode === "throttled";

    return (
        <Section icon={Clock} title="Auto-Throttle Pacing"
            description={i18n.t("wsettings.pacing_desc")}>
            <ToggleRow
                checked={isThrottled}
                onChange={(v) => save({ mode: v ? "throttled" : "immediate" })}
                disabled={busy}
                label={i18n.t("wsettings.pacing_label")}
                sub={isThrottled
                    ? i18n.t("wsettings.pacing_on", { start: pacing.office_start || "09:00", end: pacing.office_end || "17:00", tz: pacing.timezone || "Europe/Brussels", jitter: pacing.jitter_seconds || 90 })
                    : i18n.t("wsettings.pacing_off")}
                testid="pacing-mode-toggle"
            />
            {isThrottled && (
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-4 border-t border-white/8">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Office start</label>
                        <input type="time" value={pacing.office_start || "09:00"}
                            onChange={(e) => setPacing({ ...pacing, office_start: e.target.value })}
                            onBlur={(e) => save({ office_start: e.target.value })}
                            data-testid="pacing-office-start"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Office end</label>
                        <input type="time" value={pacing.office_end || "17:00"}
                            onChange={(e) => setPacing({ ...pacing, office_end: e.target.value })}
                            onBlur={(e) => save({ office_end: e.target.value })}
                            data-testid="pacing-office-end"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Jitter (sec)</label>
                        <input type="number" min={0} max={600} value={pacing.jitter_seconds ?? 90}
                            onChange={(e) => setPacing({ ...pacing, jitter_seconds: Number(e.target.value) })}
                            onBlur={(e) => save({ jitter_seconds: Number(e.target.value) })}
                            data-testid="pacing-jitter"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none" />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Tijdzone</label>
                        <select value={pacing.timezone || "Europe/Brussels"}
                            onChange={(e) => save({ timezone: e.target.value })}
                            data-testid="pacing-timezone"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                            <option value="Europe/Brussels">Europe/Brussels</option>
                            <option value="Europe/Amsterdam">Europe/Amsterdam</option>
                            <option value="Europe/Paris">Europe/Paris</option>
                            <option value="Europe/London">Europe/London</option>
                            <option value="Europe/Berlin">Europe/Berlin</option>
                            <option value="America/New_York">America/New_York</option>
                            <option value="America/Los_Angeles">America/Los_Angeles</option>
                            <option value="UTC">UTC</option>
                        </select>
                    </div>
                    <div className="sm:col-span-2 lg:col-span-4">
                        <ToggleRow
                            checked={!!pacing.respect_weekends}
                            onChange={(v) => save({ respect_weekends: v })}
                            disabled={busy}
                            label={i18n.t("wsettings.skip_weekends")}
                            sub={i18n.t("wsettings.skip_weekends_sub")}
                            testid="pacing-weekends-toggle"
                        />
                    </div>
                </div>
            )}
        </Section>
    );
}


/* ------------------------------------------------------------------ */
/*  Auto-Enrich Pipeline toggle                                        */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/*  Profile Resolver agent — LinkedIn-dekking + auto-sweep toggle      */
/* ------------------------------------------------------------------ */
function ProfileResolverSection() {
    const [stats, setStats] = useState(null);
    const [busy, setBusy] = useState(false);
    const [sweeping, setSweeping] = useState(false);

    const load = async () => {
        try { const r = await api.get("/profile-resolver/stats"); setStats(r.data); }
        catch { /* stil — sectie toont dan geen cijfers */ }
    };
    useEffect(() => { load(); }, []);

    const toggle = async (v) => {
        setBusy(true);
        try {
            await api.patch("/profile-resolver/settings", { enabled: v });
            setStats((s) => ({ ...s, auto_enabled: v }));
            toast.success(v ? i18n.t("wsettings.feature_on", { name: "Profile Resolver" }) : i18n.t("wsettings.feature_off", { name: "Profile Resolver" }));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };

    const runSweep = async () => {
        setSweeping(true);
        try {
            const r = await api.post("/profile-resolver/sweep");
            const d = r.data;
            const f = d.freshness || {};
            toast.success(
                i18n.t("wsettings.sweep_done", { found: d.found, attempted: d.attempted }) +
                (f.checked ? i18n.t("wsettings.sweep_fresh", { checked: f.checked, changes: f.changes }) : "") + "."
            );
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("wsettings.sweep_failed")); }
        finally { setSweeping(false); }
    };

    const pct = (o) => (o?.total ? Math.round((o.with_linkedin / o.total) * 100) : 0);
    const freshPct = stats?.freshness?.with_linkedin
        ? Math.round((stats.freshness.fresh / stats.freshness.with_linkedin) * 100) : 0;

    return (
        <Section icon={Linkedin} title="Profile Resolver agent"
            description={i18n.t("wsettings.resolver_desc")}>
            {stats && (
                <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="resolver-coverage">
                    {[[i18n.t("wsettings.lbl_candidates"), stats.candidates], [i18n.t("wsettings.lbl_contacts"), stats.contacts]].map(([label, o]) => (
                        <div key={label} className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3">
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
                            <div className="mt-1 text-white font-mono text-sm">{o.with_linkedin}/{o.total} <span className="text-[#0a66c2]">· {pct(o)}%</span></div>
                            <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                <div className="h-full rounded-full bg-[#0a66c2]" style={{ width: `${pct(o)}%` }} />
                            </div>
                        </div>
                    ))}
                    {stats.freshness && (
                        <div className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3" data-testid="resolver-freshness">
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Profiel-versheid (7d)</div>
                            <div className="mt-1 text-white font-mono text-sm">{stats.freshness.fresh}/{stats.freshness.with_linkedin} <span className="text-[var(--jr-success)]">· {freshPct}%</span></div>
                            <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                <div className="h-full rounded-full bg-[var(--jr-success)]" style={{ width: `${freshPct}%` }} />
                            </div>
                        </div>
                    )}
                </div>
            )}
            <ToggleRow
                checked={stats ? stats.auto_enabled : true}
                onChange={toggle}
                disabled={busy || !stats}
                label={i18n.t("wsettings.resolver_label")}
                sub={stats?.auto_enabled === false
                    ? i18n.t("wsettings.resolver_off")
                    : i18n.t("wsettings.resolver_on")}
                testid="resolver-auto-toggle"
            />
            <div className="mt-3">
                <button onClick={runSweep} disabled={sweeping} data-testid="resolver-sweep-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium border bg-[#0a66c2]/10 text-[#0a66c2] border-[#0a66c2]/30 hover:bg-[#0a66c2]/20 disabled:opacity-50">
                    {sweeping ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    {sweeping ? i18n.t("wsettings.sweep_running") : i18n.t("wsettings.sweep_run_now")}
                </button>
            </div>
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Auto-Enrich Pipeline toggle                                        */
/* ------------------------------------------------------------------ */
function AutoEnrichSection({ workspace, refresh }) {
    const [busy, setBusy] = useState(false);
    const enabled = workspace?.auto_enrich_enabled !== false;  // default ON
    const toggle = async (v) => {
        setBusy(true);
        try {
            await api.patch("/workspace-settings", { auto_enrich_enabled: v });
            await refresh?.();
            toast.success(v ? i18n.t("wsettings.feature_on", { name: "Auto-Enrich" }) : i18n.t("wsettings.feature_off", { name: "Auto-Enrich" }));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    return (
        <Section icon={Wand2} title="Auto-Enrich Pipeline"
            description={i18n.t("wsettings.surfe_desc")}>
            <ToggleRow
                checked={enabled}
                onChange={toggle}
                disabled={busy}
                label={i18n.t("wsettings.surfe_label")}
                sub={enabled
                    ? i18n.t("wsettings.surfe_on")
                    : i18n.t("wsettings.surfe_off")}
                testid="auto-enrich-toggle"
            />
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Auto-Draft Outreach toggle                                         */
/* ------------------------------------------------------------------ */
function AutoDraftSection({ workspace, refresh }) {
    const [busy, setBusy] = useState(false);
    const enabled = workspace?.auto_draft_enabled !== false;  // default ON
    const toggle = async (v) => {
        setBusy(true);
        try {
            await api.patch("/workspace-settings", { auto_draft_enabled: v });
            await refresh?.();
            toast.success(v ? i18n.t("wsettings.feature_on", { name: "Auto-Draft" }) : i18n.t("wsettings.feature_off", { name: "Auto-Draft" }));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    return (
        <Section icon={FileEdit} title="Auto-Draft Outreach"
            description={i18n.t("wsettings.draft_desc")}>
            <ToggleRow
                checked={enabled}
                onChange={toggle}
                disabled={busy}
                label={i18n.t("wsettings.draft_label")}
                sub={enabled
                    ? i18n.t("wsettings.draft_on")
                    : i18n.t("wsettings.draft_off")}
                testid="auto-draft-toggle"
            />
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Contact-taal auto-detectie · info + summary (user choice 2b)      */
/* ------------------------------------------------------------------ */
function LanguageBar({ label, data }) {
    if (!data) return null;
    const total = Math.max(1, Object.values(data).reduce((a, b) => a + b, 0));
    const colors = { nl: "#FF5A00", en: "#0891b2", fr: "#a855f7", unset: "#475569" };
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1">{label}</div>
            <div className="w-full h-2 rounded-full overflow-hidden bg-white/5 flex">
                {Object.entries(data).map(([k, v]) => (
                    <div key={k} style={{ width: `${(v / total) * 100}%`, backgroundColor: colors[k] || "#475569" }}
                        title={`${k}: ${v} (${Math.round((v/total)*100)}%)`}/>
                ))}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-[var(--jr-text-2)]">
                {Object.entries(data).map(([k, v]) => (
                    <span key={k}>
                        <span className="inline-block w-2 h-2 rounded-full align-middle mr-1" style={{ backgroundColor: colors[k] || "#475569" }}/>
                        {k.toUpperCase()} · {v}
                    </span>
                ))}
            </div>
        </div>
    );
}

function LanguageSection({ workspace, refresh }) {
    const [summary, setSummary] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/language/summary");
                setSummary(data);
            } catch (_e) { /* silent */ }
        })();
    }, []);
    return (
        <Section icon={Languages} title={i18n.t("wsettings.lang_title")}
            description={i18n.t("wsettings.lang_desc")}>
            <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-[var(--jr-brand)]/30 bg-[var(--jr-brand)]/[0.04] p-4">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--jr-brand)] mb-1">LinkedIn</div>
                    <div className="text-sm text-white font-medium">{i18n.t("wsettings.lang_profile_title")}</div>
                    <div className="text-[11px] text-[var(--jr-text-2)] mt-1">{i18n.t("wsettings.lang_profile_sub")}</div>
                </div>
                <div className="rounded-xl border border-[#a855f7]/30 bg-[#a855f7]/[0.04] p-4">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#a855f7] mb-1">LinkedIn posts</div>
                    <div className="text-sm text-white font-medium">{i18n.t("wsettings.lang_posts_title")}</div>
                    <div className="text-[11px] text-[var(--jr-text-2)] mt-1">{i18n.t("wsettings.lang_posts_sub")}</div>
                </div>
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-4">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-400 mb-1">Reply</div>
                    <div className="text-sm text-white font-medium">{i18n.t("wsettings.lang_reply_title")}</div>
                    <div className="text-[11px] text-[var(--jr-text-2)] mt-1">{i18n.t("wsettings.lang_reply_sub")}</div>
                </div>
            </div>
            {summary && (
                <div className="mt-5 grid sm:grid-cols-2 gap-4 pt-4 border-t border-white/8" data-testid="language-summary">
                    <LanguageBar label="Leads" data={summary.leads}/>
                    <LanguageBar label={i18n.t("wsettings.lbl_candidates")} data={summary.candidates}/>
                </div>
            )}
        </Section>
    );
}


/* ------------------------------------------------------------------ */
/*  Sprint 5 · #18 Auto-approval van veilige drafts                   */
/* ------------------------------------------------------------------ */
function AutoApprovalSection() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = async () => {
        try {
            const { data } = await api.get("/outreach/settings/auto-approval");
            setCfg(data);
        } catch (e) { /* ignore */ }
    };
    useEffect(() => { load(); }, []);
    const save = async (changes) => {
        setBusy(true);
        try {
            const { data } = await api.patch("/outreach/settings/auto-approval", changes);
            setCfg({ ...cfg, ...data });
            toast.success(i18n.t("wsettings.autoapprove_updated"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    if (!cfg) return null;
    const enabled = !!cfg.auto_approval_enabled;
    return (
        <Section icon={Zap} title={i18n.t("wsettings.autoapprove_title")}
            description={i18n.t("wsettings.autoapprove_desc")}>
            <ToggleRow
                checked={enabled}
                onChange={(v) => save({ auto_approval_enabled: v })}
                disabled={busy}
                label={i18n.t("wsettings.autoapprove_label")}
                sub={enabled
                    ? i18n.t("wsettings.autoapprove_on")
                    : i18n.t("wsettings.autoapprove_off")}
                testid="auto-approval-toggle"
            />
            {enabled && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3 pt-4 border-t border-white/8">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Minimum safety-score</label>
                        <div className="mt-1 flex items-center gap-3">
                            <input type="range" min={50} max={100} step={5}
                                defaultValue={cfg.auto_approval_min_confidence}
                                onMouseUp={(e) => save({ auto_approval_min_confidence: Number(e.currentTarget.value) })}
                                onTouchEnd={(e) => save({ auto_approval_min_confidence: Number(e.currentTarget.value) })}
                                data-testid="auto-approval-threshold"
                                className="flex-1"/>
                            <span className="text-sm text-white font-mono w-10 text-right">{cfg.auto_approval_min_confidence}%</span>
                        </div>
                        <div className="text-[10px] text-[var(--jr-text-3)] mt-1">
                            85% = alleen voorzichtige auto-approvals. 70% = ruimhartiger.
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Daglimiet</label>
                        <input type="number" min={0} max={500}
                            defaultValue={cfg.auto_approval_max_per_day}
                            onBlur={(e) => save({ auto_approval_max_per_day: Number(e.currentTarget.value) })}
                            data-testid="auto-approval-daily-cap"
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
                        <div className="text-[10px] text-[var(--jr-text-3)] mt-1">Hard-cap per workspace/dag.</div>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Verboden woorden (comma-separated)</label>
                        <input type="text"
                            defaultValue={(cfg.auto_approval_forbidden_phrases || []).join(", ")}
                            onBlur={(e) => save({ auto_approval_forbidden_phrases: e.currentTarget.value.split(",").map(s => s.trim()).filter(Boolean) })}
                            data-testid="auto-approval-forbidden"
                            placeholder="urgent!!!, gegarandeerd, klik hier, ..."
                            className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
                        <div className="text-[10px] text-[var(--jr-text-3)] mt-1">
                            Elke draft die één van deze bevat wordt NOOIT auto-approved.
                        </div>
                    </div>
                </div>
            )}
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Sprint 5 · #9 Cronos re-engage                                    */
/* ------------------------------------------------------------------ */
function CronosReengageSection({ workspace, refresh }) {
    const [busy, setBusy] = useState(false);
    const [digestBusy, setDigestBusy] = useState(false);
    const enabled = workspace?.cronos_reengage_enabled !== false;
    const realtime = workspace?.cronos_reengage_realtime !== false;
    const monthly = workspace?.cronos_reengage_monthly_digest !== false;
    const patch = async (changes) => {
        setBusy(true);
        try {
            await api.patch("/workspace-settings", changes);
            await refresh?.();
            toast.success(i18n.t("wsettings.reengage_updated"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    const sendDigest = async () => {
        setDigestBusy(true);
        try {
            const { data } = await api.post("/reengage/cronos/monthly-digest");
            if (data?.sent) toast.success(i18n.t("wsettings.digest_sent", { candidates: data.candidates, matches: data.matches }));
            else toast.info(i18n.t("wsettings.digest_none"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setDigestBusy(false); }
    };
    return (
        <Section icon={HeartHandshake} title="Cronos re-engage"
            description={i18n.t("wsettings.reengage_desc")}>
            <ToggleRow
                checked={enabled}
                onChange={(v) => patch({ cronos_reengage_enabled: v })}
                disabled={busy}
                label={i18n.t("wsettings.reengage_label")}
                sub={enabled ? i18n.t("wsettings.reengage_on") : i18n.t("wsettings.reengage_off")}
                testid="cronos-reengage-toggle"
            />
            {enabled && (
                <div className="mt-3 space-y-3">
                    <ToggleRow
                        checked={realtime}
                        onChange={(v) => patch({ cronos_reengage_realtime: v })}
                        disabled={busy}
                        label={i18n.t("wsettings.reengage_realtime_label")}
                        sub={realtime
                            ? i18n.t("wsettings.reengage_scan")
                            : i18n.t("wsettings.reengage_realtime_off")}
                        testid="cronos-realtime-toggle"
                    />
                    <ToggleRow
                        checked={monthly}
                        onChange={(v) => patch({ cronos_reengage_monthly_digest: v })}
                        disabled={busy}
                        label={i18n.t("wsettings.monthly_digest")}
                        sub={monthly
                            ? i18n.t("wsettings.monthly_digest_sub")
                            : i18n.t("wsettings.off_short")}
                        testid="cronos-monthly-toggle"
                    />
                    <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-white/8">
                        <div className="flex-1 min-w-0">
                            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Minimum match confidence (%)</label>
                            <div className="mt-1 flex items-center gap-3">
                                <input type="range" min={40} max={95} step={5}
                                    defaultValue={workspace?.cronos_reengage_min_confidence ?? 65}
                                    onMouseUp={(e) => patch({ cronos_reengage_min_confidence: Number(e.currentTarget.value) })}
                                    onTouchEnd={(e) => patch({ cronos_reengage_min_confidence: Number(e.currentTarget.value) })}
                                    data-testid="cronos-min-conf"
                                    className="flex-1"/>
                                <span className="text-sm text-white font-mono w-10 text-right">{workspace?.cronos_reengage_min_confidence ?? 65}%</span>
                            </div>
                        </div>
                        <button onClick={sendDigest} disabled={digestBusy}
                            data-testid="cronos-send-digest"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                            {digestBusy ? <Loader2 className="animate-spin" size={14}/> : <UsersIcon size={14}/>}
                            {i18n.t("wsettings.send_digest_now")}
                        </button>
                    </div>
                </div>
            )}
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Sprint 5 · #17 Pre-warm outreach                                  */
/* ------------------------------------------------------------------ */
function PrewarmSection({ workspace, refresh }) {
    const [busy, setBusy] = useState(false);
    const enabled = workspace?.prewarm_enabled !== false;
    const toggle = async (v) => {
        setBusy(true);
        try {
            await api.patch("/workspace-settings", { prewarm_enabled: v });
            await refresh?.();
            toast.success(v ? i18n.t("wsettings.feature_on", { name: "Pre-warm" }) : i18n.t("wsettings.feature_off", { name: "Pre-warm" }));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    return (
        <Section icon={UserCheck} title={i18n.t("wsettings.prewarm_title")}
            description={i18n.t("wsettings.prewarm_desc")}>
            <ToggleRow
                checked={enabled}
                onChange={toggle}
                disabled={busy}
                label={i18n.t("wsettings.prewarm_label")}
                sub={enabled
                    ? i18n.t("wsettings.prewarm_on")
                    : i18n.t("wsettings.prewarm_off")}
                testid="prewarm-toggle"
            />
        </Section>
    );
}

/* ------------------------------------------------------------------ */
/*  Sprint 4b · #15 Diversity-lens defaults                           */
/* ------------------------------------------------------------------ */
function DiversityLensSection({ workspace, refresh }) {
    const [busy, setBusy] = useState(false);
    const dl = workspace?.diversity_lens || {};
    const patch = async (changes) => {
        setBusy(true);
        try {
            await api.patch("/workspace-settings", { diversity_lens: { ...dl, ...changes } });
            await refresh?.();
            toast.success(i18n.t("wsettings.diversity_updated"));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    return (
        <Section icon={UsersIcon} title={i18n.t("wsettings.diversity_title")}
            description={i18n.t("wsettings.diversity_desc")}>
            <ToggleRow
                checked={!!dl.enabled}
                onChange={(v) => patch({ enabled: v })}
                disabled={busy}
                label={i18n.t("wsettings.diversity_label")}
                sub={dl.enabled
                    ? i18n.t("wsettings.diversity_on")
                    : i18n.t("wsettings.diversity_off")}
                testid="diversity-enabled-toggle"
            />
            {dl.enabled && (
                <div className="mt-3 space-y-2 pt-3 border-t border-white/8">
                    <ToggleRow
                        checked={dl.boost_underrepresented_gender !== false}
                        onChange={(v) => patch({ boost_underrepresented_gender: v })}
                        disabled={busy}
                        label={i18n.t("wsettings.div_gender_label")}
                        sub={i18n.t("wsettings.div_gender_sub")}
                        testid="div-gender-toggle"
                    />
                    <ToggleRow
                        checked={dl.balance_seniority !== false}
                        onChange={(v) => patch({ balance_seniority: v })}
                        disabled={busy}
                        label={i18n.t("wsettings.div_seniority_label")}
                        sub={i18n.t("wsettings.div_seniority_sub")}
                        testid="div-seniority-toggle"
                    />
                    <ToggleRow
                        checked={!!dl.balance_regions}
                        onChange={(v) => patch({ balance_regions: v })}
                        disabled={busy}
                        label={i18n.t("wsettings.div_region_label")}
                        sub={i18n.t("wsettings.div_region_sub")}
                        testid="div-region-toggle"
                    />
                </div>
            )}
        </Section>
    );
}


/* ------------------------------------------------------------------ */
/*  Sprint 6 · #20 Rate-limit governor                                */
/* ------------------------------------------------------------------ */
function GovernorSection() {
    const [state, setState] = useState({ loading: true, providers: [] });
    const [busy, setBusy] = useState(false);
    const load = async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
            const { data } = await api.get("/sprint6/governor/usage");
            setState({ loading: false, providers: data.providers || [] });
        } catch (_govErr) { setState({ loading: false, providers: [] }); }
    };
    useEffect(() => { load(); }, []);
    const patchBudget = async (provider, val) => {
        setBusy(true);
        try {
            const next = Object.fromEntries(state.providers.map(p => [p.provider, p.provider === provider ? val : p.budget]));
            await api.patch("/sprint6/governor/budgets", { budgets: next });
            await load();
            toast.success(`${provider} budget: ${val}/dag.`);
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    return (
        <Section icon={Gauge} title="Rate-limit governor (spend-guard)"
            description={i18n.t("wsettings.budget_desc")}>
            {state.loading ? (
                <div className="text-xs text-[var(--jr-text-3)]"><Loader2 className="animate-spin inline mr-1" size={11}/>{i18n.t("common.loading")}</div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="governor-grid">
                    {state.providers.map((p) => (
                        <div key={p.provider} className="rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/50 p-3" data-testid={`gov-${p.provider}`}>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{p.provider}</span>
                                <span className={`text-[10px] font-mono ${p.pct >= 80 ? "text-red-400" : p.pct >= 50 ? "text-amber-400" : "text-emerald-400"}`}>
                                    {p.pct}%
                                </span>
                            </div>
                            <div className="text-sm text-white">{p.used} <span className="text-[var(--jr-text-3)]">/ {p.budget}</span></div>
                            <div className="mt-1.5 w-full h-1 rounded-full bg-white/5 overflow-hidden">
                                <div className={`h-full ${p.pct >= 80 ? "bg-red-500" : p.pct >= 50 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, p.pct)}%` }}/>
                            </div>
                            <input type="number" min={0} max={100000} defaultValue={p.budget}
                                onBlur={(e) => patchBudget(p.provider, Number(e.currentTarget.value))}
                                disabled={busy}
                                data-testid={`gov-budget-${p.provider}`}
                                className="mt-2 w-full bg-[var(--jr-bg)] border border-white/10 rounded-md px-2 py-1 text-xs text-white outline-none focus:border-[var(--jr-brand)]"/>
                        </div>
                    ))}
                </div>
            )}
        </Section>
    );
}


/* ------------------------------------------------------------------ */
/*  Sprint 6 · #21 GDPR auto-purge                                    */
/* ------------------------------------------------------------------ */
function GdprSection({ workspace, refresh }) {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = async () => {
        try { const { data } = await api.get("/sprint6/gdpr/scheduled"); setState(data); }
        catch (_gdpr) { setState({ retention_days: 730, auto_purge_enabled: false, leads_pending: 0, candidates_pending: 0 }); }
    };
    useEffect(() => { load(); }, []);
    const toggle = async (v) => {
        setBusy(true);
        try {
            await api.patch("/workspace-settings", { gdpr_auto_purge_enabled: v });
            await refresh?.();
            await load();
            toast.success(v ? i18n.t("wsettings.feature_on", { name: "GDPR auto-purge" }) : i18n.t("wsettings.feature_off", { name: "GDPR auto-purge" }));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    const setRetention = async (days) => {
        setBusy(true);
        try {
            await api.patch("/workspace-settings", { gdpr_retention_days: Math.max(30, days) });
            await refresh?.();
            await load();
            toast.success(i18n.t("wsettings.retention_set", { days }));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    const purgeNow = async (dry) => {
        setBusy(true);
        try {
            const { data } = await api.post("/sprint6/gdpr/purge", { dry_run: dry });
            if (dry) toast.info(i18n.t("wsettings.purge_dry", { leads: data.leads_would_purge, candidates: data.candidates_would_purge }));
            else toast.success(i18n.t("wsettings.purge_done", { leads: data.leads_purged, candidates: data.candidates_purged }));
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    if (!state) return null;
    return (
        <Section icon={Shield} title="GDPR auto-purge"
            description={i18n.t("wsettings.gdpr_desc")}>
            <ToggleRow
                checked={!!state.auto_purge_enabled}
                onChange={toggle}
                disabled={busy}
                label={i18n.t("wsettings.gdpr_label")}
                sub={state.auto_purge_enabled
                    ? i18n.t("wsettings.gdpr_on")
                    : i18n.t("wsettings.gdpr_off")}
                testid="gdpr-toggle"
            />
            <div className="mt-3 flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                    <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{i18n.t("wsettings.retention_days")}</label>
                    <input type="number" min={30} max={3650} defaultValue={state.retention_days}
                        onBlur={(e) => setRetention(Number(e.currentTarget.value))}
                        disabled={busy}
                        data-testid="gdpr-retention"
                        className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"/>
                    <div className="text-[10px] text-[var(--jr-text-3)] mt-1">
                        Default: 730d (2 jaar) · GDPR-safe voor B2B outreach data.
                    </div>
                </div>
                <button onClick={() => purgeNow(true)} disabled={busy} data-testid="gdpr-dry-run"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">
                    <Sparkles size={13}/> Dry-run
                </button>
                <button onClick={() => { if (window.confirm(i18n.t("wsettings.purge_confirm", { leads: state.leads_pending, candidates: state.candidates_pending }))) purgeNow(false); }}
                    disabled={busy || (state.leads_pending + state.candidates_pending) === 0}
                    data-testid="gdpr-purge-now"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500 text-white text-sm hover:bg-red-600 disabled:opacity-50">
                    <Trash2 size={13}/> Purge nu ({state.leads_pending + state.candidates_pending})
                </button>
            </div>
            <div className="mt-3 pt-3 border-t border-white/8 text-[11px] text-[var(--jr-text-2)]">
                <strong className="text-white">{state.leads_pending}</strong> leads · <strong className="text-white">{state.candidates_pending}</strong> {i18n.t("wsettings.purge_pending")}
            </div>
        </Section>
    );
}


/* ------------------------------------------------------------------ */
/*  Sprint 6 · #5 Auto-ICP + #19 Freshness                            */
/* ------------------------------------------------------------------ */
function Sprint6ExtrasSection({ workspace, refresh }) {
    const { t } = useTranslation();
    const [icp, setIcp] = useState(null);
    const [busy, setBusy] = useState(false);
    const [staleCount, setStaleCount] = useState(null);
    useEffect(() => {
    }, [workspace]);
    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/sprint6/icp/suggested"); setIcp(data); } catch (_e1) { /* silent */ }
            try { const { data } = await api.get("/sprint6/freshness/stale"); setStaleCount(data.count); } catch (_e2) { /* silent */ }
        })();
    }, []);
    const learnIcp = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/sprint6/icp/learn");
            if (data.skipped) toast.info(data.reason || i18n.t("wsettings.not_enough_data"));
            else { toast.success(i18n.t("wsettings.icp_learned")); setIcp({ suggested: data, last_learn_at: data.generated_at }); }
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    const refreshFresh = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/sprint6/freshness/refresh-all");
            toast.success(i18n.t("wsettings.rescored", { updated: data.updated, stale: data.stale_count }));
            setStaleCount(data.stale_count);
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setBusy(false); }
    };
    const sug = icp?.suggested || {};
    return (
        <Section icon={Brain} title="Auto-ICP · Data-freshness"
            description={i18n.t("wsettings.icp_desc")}>
            <div className="grid gap-3 md:grid-cols-2">
                {/* Auto-ICP card */}
                <div className="rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/50 p-4" data-testid="icp-card">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-brand)]">#5 · Auto-ICP</span>
                        <button onClick={learnIcp} disabled={busy} data-testid="icp-learn-btn"
                            className="text-[10px] font-mono text-[var(--jr-brand)] hover:underline">
                            {busy ? "…" : i18n.t("wsettings.learn_now")}
                        </button>
                    </div>
                    {sug.industry || sug.size || sug.country || sug.role ? (
                        <div className="space-y-1 text-xs">
                            {sug.industry && <div><span className="text-[var(--jr-text-3)]">Industry:</span> <span className="text-white">{sug.industry}</span></div>}
                            {sug.size && <div><span className="text-[var(--jr-text-3)]">Size:</span> <span className="text-white">{sug.size}</span></div>}
                            {sug.country && <div><span className="text-[var(--jr-text-3)]">Country:</span> <span className="text-white">{sug.country}</span></div>}
                            {sug.role && <div><span className="text-[var(--jr-text-3)]">Role:</span> <span className="text-white">{sug.role}</span></div>}
                            <div className="text-[10px] text-[var(--jr-text-3)] mt-2 pt-2 border-t border-white/8">
                                {i18n.t("wsettings.based_on_won", { count: sug.based_on_won_count || 0 })}
                            </div>
                        </div>
                    ) : (
                        <div className="text-[11px] text-[var(--jr-text-3)] italic">
                            {i18n.t("wsettings.icp_none")}
                        </div>
                    )}
                </div>
                {/* Freshness card */}
                <div className="rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/50 p-4" data-testid="freshness-card">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#a855f7]">#19 · Freshness</span>
                        <button onClick={refreshFresh} disabled={busy} data-testid="freshness-refresh-btn"
                            className="text-[10px] font-mono text-[#a855f7] hover:underline">
                            {busy ? "…" : i18n.t("wsettings.refresh_now")}
                        </button>
                    </div>
                    <div className="text-2xl font-display text-white">{staleCount ?? "—"}</div>
                    <div className="text-[11px] text-[var(--jr-text-3)] mt-1">
                        {i18n.t("wsettings.stale_info")}
                    </div>
                </div>
                </div>
        </Section>
    );
}